import { useAuth, useOrganization, useOrganizationList } from "@clerk/react";
import { useEffect, useRef } from "react";

import { getClerkToken } from "../clerkTokenBridge";
import { refreshEntitlements } from "../morrowEntitlements";

/** Decode a JWT payload without verifying — client diagnostic only. */
function peekJwt(token: string | null): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    return JSON.parse(atob(part.replace(/-/g, "+").replace(/_/g, "/"))) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Auto-activate the caller's organization.
 *
 * Clerk only stamps `org_id`/`org_role` into the session JWT when an org is the
 * ACTIVE org — being a member is not enough. MorrowOS is business-first: a user
 * who belongs to exactly one org should operate AS that org without manually
 * switching, so the backend resolves their COMPANY plan (seats, org-shared
 * storage, company usage) instead of an individual one.
 *
 * So: when signed in, memberships loaded, no active org, and exactly one
 * membership, activate it once. After activation the auth'd fetch layer sends a
 * fresh token carrying the org claims, so we re-pull entitlements to flip the
 * plan over. We never override a user who has deliberately picked personal or who
 * belongs to several orgs (they choose via the switcher).
 */
export function OrgAutoActivate() {
  const { isLoaded: orgLoaded, organization } = useOrganization();
  const {
    isLoaded: listLoaded,
    setActive,
    userMemberships,
  } = useOrganizationList({
    userMemberships: true,
  });
  const { getToken } = useAuth();
  const activatedRef = useRef(false);
  const probedRef = useRef(false);

  // One-shot token probe: does the Clerk token carry the org claim, and does the
  // bridge (what the API layer actually sends) return a token at all?
  useEffect(() => {
    if (probedRef.current || !organization) return;
    probedRef.current = true;
    void (async () => {
      const direct = await getToken().catch(() => null);
      const viaBridge = await getClerkToken().catch(() => null);
      const claims = peekJwt(direct);
      console.warn("[morrow][OrgTokenProbe]", {
        activeOrgId: organization.id,
        directTokenPresent: !!direct,
        bridgeTokenPresent: !!viaBridge,
        directHasOrgClaim: !!(claims && (claims["org_id"] || claims["o"])),
        directOrgClaim: claims?.["org_id"] ?? claims?.["o"] ?? null,
      });
    })();
  }, [organization, getToken]);

  useEffect(() => {
    const memberships = userMemberships.data ?? [];
    // Loud diagnostic (captured in the dev log) so we can see exactly why the org
    // does/doesn't activate. Remove once org routing is verified.
    console.warn("[morrow][OrgAutoActivate]", {
      orgLoaded,
      listLoaded,
      hasSetActive: !!setActive,
      activeOrgId: organization?.id ?? null,
      membershipCount: memberships.length,
      membershipOrgIds: memberships.map((m) => m.organization?.id),
      alreadyActivated: activatedRef.current,
    });
    if (!orgLoaded || !listLoaded) return;
    // Org already active: the startup entitlements fetch likely ran BEFORE the
    // Clerk token was ready (owner id is sync, the token is async), so it went out
    // tokenless → resolved as individual. Re-pull once now that the token exists so
    // the backend sees the org and returns the company plan.
    if (organization) {
      if (!activatedRef.current) {
        activatedRef.current = true;
        console.warn("[morrow][OrgAutoActivate] org already active — refetching entitlements");
        void refreshEntitlements();
      }
      return;
    }
    if (activatedRef.current) return;
    if (!setActive) return;
    if (memberships.length < 1) return; // personal only — nothing to activate
    const only = memberships[0]?.organization;
    if (!only) return;
    activatedRef.current = true;
    console.warn("[morrow][OrgAutoActivate] activating org", only.id);
    void setActive({ organization: only.id })
      .then(() => {
        console.warn("[morrow][OrgAutoActivate] setActive OK, refreshing entitlements");
        return refreshEntitlements();
      })
      .catch((err) => {
        console.warn("[morrow][OrgAutoActivate] setActive FAILED", String(err));
        activatedRef.current = false; // allow a retry on transient failure
      });
  }, [orgLoaded, listLoaded, setActive, organization, userMemberships.data]);

  return null;
}
